import React, { useState, useEffect, useRef } from "react";
import { API, graphqlOperation } from "aws-amplify";
import * as queries from "../../../graphql/queries";
import { useNotify, useRedirect } from "react-admin";
import { formatDistanceToNow } from "date-fns";
import { makeStyles } from "@material-ui/core/styles";
import { Title } from "react-admin";
import { Route, Link } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  CardContent,
  CardHeader,
  CardActions,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  Backdrop,
} from "@material-ui/core";
import { amber, green, red } from "@material-ui/core/colors";
import {
  CloseIcon,
  ChevronRightIcon,
  TaskItemOutlinedIcon,
  TaskListOutlinedIcon,
  RefreshIcon,
  AttachFileIcon,
} from "../../../components/common/iconography";
import ErrorCard from "../../../components/ErrorCard";
import { logError } from "../../../utils/appUtils";
import { getTaskPriorityText, getTaskStatusText } from "../../../utils/utils";
import { updateTask } from "../../../graphql/mutations";
import CrewCard from "../../../components/common/CrewCard";
import { FileField } from "../../../components/common/FileField";

const useStyles = makeStyles((theme) => ({
  taskSwitch: {
    marginTop: theme.spacing(1.25),
    marginRight: theme.spacing(2),
  },
  link: {
    color: "inherit",
    textDecoration: "inherit",
  },
  notFound: {
    display: "flex",
    margin: theme.spacing(2),
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(2),
  },
  green: {
    color: theme.palette.getContrastText(green[200]),
    backgroundColor: green[200],
  },
  amber: {
    color: theme.palette.getContrastText(amber[100]),
    backgroundColor: amber[100],
  },
  red: {
    color: theme.palette.getContrastText(red[100]),
    backgroundColor: red[100],
  },
  backdrop: {
    zIndex: 90001,
  },
  inputField: {
    width: "50%",
  },
  topActions: {
    paddingTop: theme.spacing(2),
    display: "flex",
    alignItems: "center",
  },
}));

export const Tasks = ({ match, history, redirectTo, hideTitle }) => {
  const [showAllTasks, setShowAllTasks] = useState(false);
  const [loading, setLoading] = useState();
  const isComponentMounted = useRef(true);
  const [tasks, setTasks] = useState([]);
  const classes = useStyles();
  const notify = useNotify();
  if (!redirectTo) redirectTo = history.location.state?.redirectTo;

  useEffect(() => {
    if (isComponentMounted && window.appUser) {
      setLoading(true);
      getTasks();
    }

    return () => {
      isComponentMounted.current = false;
    };
  }, []);

  const getTasks = async (allTasks) => {
    const input = {
      assignedTo: window.appUser?.id,
      statusDueDate: {
        lt: {
          status: "21",
        },
      },
    };
    if (allTasks) {
      input.statusDueDate = {
        lt: {
          status: "41",
        },
      };
    }
    try {
      const response = await API.graphql(
        graphqlOperation(queries.taskByAssignedToByStatusByDueDate, input)
      );
      const tasksResponse =
        response.data.taskByAssignedToByStatusByDueDate.items;
      setTasks(tasksResponse);
    } catch (err) {
      logError("Tasks:getTasks", err, notify);
    } finally {
      setLoading(false);
    }
  };

  const toggleChecked = () => {
    debugger;
    const value = !showAllTasks;
    setShowAllTasks(value);
    getTasks(value);
  };

  return loading ? (
    <Backdrop className={classes.backdrop} open={loading}>
      <CircularProgress color="inherit" />
    </Backdrop>
  ) : (
    <>
      {!hideTitle && <Title title="My Tasks" />}
      <Route
        path={`${match.path}`}
        exact
        render={(props) => (
          <TaskList
            classes={classes}
            tasks={tasks}
            toggleChecked={toggleChecked}
            showAllTasks={showAllTasks}
            {...props}
            redirectTo={redirectTo}
            refresh={getTasks}
          />
        )}
      />
      <Route
        path={`${match.path}/:id`}
        render={(props) => (
          <TaskItem
            {...props}
            setTasks={setTasks}
            tasks={tasks}
            classes={classes}
            redirectTo={redirectTo}
          />
        )}
      />
    </>
  );
};

const TaskList = ({
  history,
  tasks,
  toggleChecked,
  showAllTasks,
  redirectTo,
  refresh,
}) => {
  const classes = useStyles();
  const isXSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));

  const getAvatarClassName = (task) => {
    if (task.priority === 2) return classes.amber;
    if (task.priority === 3) return classes.red;
    return classes.avatar;
  };

  const handleClick = (task) => {
    history.push(`/mytasks/${task.id}`);
  };

  return (
    <CrewCard
      title="My Tasks"
      icon={<TaskListOutlinedIcon fontSize="large" />}
      addMargin={true}
      action={
        <div className={classes.topActions}>
          {!isXSmall && (
            <FormControl>
              <FormControlLabel
                label="All Tasks"
                labelPlacement="start"
                control={
                  <Switch
                    color={showAllTasks ? "secondary" : "default"}
                    checked={showAllTasks}
                    onChange={toggleChecked}
                  />
                }
              />
            </FormControl>
          )}
          <IconButton onClick={refresh}>
            <RefreshIcon fontSize="large" />
          </IconButton>
        </div>
      }
    >
      {tasks?.length > 0 ? (
        <List>
          {tasks.map((task, idx) => {
            return (
              <div key={task.id}>
                <Link
                  className={classes.link}
                  to={{
                    pathname: `/mytasks/${task.id}`,
                    state: { task, redirectTo },
                  }}
                >
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar className={getAvatarClassName(task)}>
                        <TaskItemOutlinedIcon fontSize="inherit" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={task.name}
                      secondary={task.description}
                    />
                    <ListItemSecondaryAction>
                      <IconButton onClick={() => handleClick(task)}>
                        <ChevronRightIcon fontSize="large" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </Link>
                {idx < tasks.length - 1 ? <Divider /> : null}
              </div>
            );
          })}
        </List>
      ) : (
        <Typography className={classes.notFound}>No tasks found.</Typography>
      )}
    </CrewCard>
  );
};

const TaskItem = ({
  tasks,
  setTasks,
  match,
  history,
  classes,
  redirectTo = "/mytasks",
}) => {
  const [task, setTask] = useState(history.location.state?.task);
  const [invalidIdError, setInvalidIdError] = useState();
  const notify = useNotify();
  const redirect = useRedirect();
  const isXSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  debugger;

  useEffect(() => {
    if (!task && tasks.length > 0 && match.params.id) {
      const selectedTask = tasks.filter((itm) => itm.id === match.params.id);
      if (selectedTask.length === 1) {
        setTask(selectedTask[0]);
      } else {
        setInvalidIdError(`Task ID ${match.params.id} doesn't exist.`);
      }
    }
  }, [match.params.id, tasks, task]);

  const handleProcess = () => {
    if (task.link) {
      history.push(task.link);
    }
  };

  const handleComplete = async () => {
    try {
      debugger;
      task.status = 30;
      task.completedDate = new Date().toISOString();
      await saveTask(task);
      setTasks(tasks.filter((itm) => itm.id !== task.id));
      history.push(redirectTo);
    } catch (err) {
      logError("TaskItem:handleComplete", err, notify, redirect);
    }
  };

  const saveTask = async (task) => {
    debugger;
    delete task.updatedAt;
    await API.graphql(graphqlOperation(updateTask, { input: task }));
  };

  const handleSave = async (task) => {
    try {
      debugger;
      await saveTask(task);
      setTasks(tasks.map((itm) => (itm.id === task.id ? task : itm)));
      history.push(redirectTo);
    } catch (err) {
      logError("TaskItem:handleSave", err, notify, redirect);
    }
  };

  const handleCancel = () => {
    debugger;
    history.replace(redirectTo);
  };

  const setNote = (evt) => {
    setTask({ ...task, note: evt.target.value });
  };

  return invalidIdError ? (
    <ErrorCard error={invalidIdError} />
  ) : task ? (
    <CrewCard>
      <CardHeader
        title={task.name}
        subheader={`Due in ${formatDistanceToNow(new Date(task.dueDate))}`}
        className={classes.listHeader}
        action={
          <IconButton edge="start" onClick={() => handleCancel()}>
            <CloseIcon fontSize="large" />
          </IconButton>
        }
      />
      <CardContent style={{ paddingBottom: 0 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography gutterBottom>{task.description}</Typography>
          </Grid>
          <Grid item sm={6} xs={12}>
            <TextField
              name="priority"
              label="Priority"
              fullWidth
              className={!isXSmall ? classes.inputField : null}
              margin="dense"
              variant="outlined"
              value={getTaskPriorityText(task.priority)}
              disabled
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <TextField
              name="status"
              label="Status"
              margin="dense"
              variant="outlined"
              fullWidth
              className={!isXSmall ? classes.inputField : null}
              value={getTaskStatusText(task.status)}
              disabled
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <TextField
              name="duedate"
              label="Due date"
              margin="dense"
              type="date"
              fullWidth
              className={!isXSmall ? classes.inputField : null}
              variant="outlined"
              value={task.dueDate}
              disabled
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <TextField
              name="latedate"
              label="Late date"
              margin="dense"
              type="date"
              fullWidth
              className={!isXSmall ? classes.inputField : null}
              variant="outlined"
              value={task.lateDate}
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="notes"
              id="outlined-multiline-static"
              label="Note"
              multiline
              minRows={3}
              margin="dense"
              variant="outlined"
              value={task.note ? task.note : ""}
              fullWidth
              onChange={setNote}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography gutterBottom>Attachments</Typography>
            {task?.attachments &&
              task.attachments.map((file) => (
                <Box display="flex" alignItems="center">
                  <AttachFileIcon fontSize="small" />
                  <FileField
                    source="file"
                    record={file}
                    src="url"
                    title="desc"
                    target="_blank"
                  />
                </Box>
              ))}
          </Grid>
        </Grid>
      </CardContent>
      <CardActions className={classes.actions}>
        <Button onClick={() => handleSave(task)} size="large">
          Save
        </Button>
        {Number(task.status) <= 20 && (
          <div>
            {task.link && (
              <Button onClick={handleProcess} color="secondary" size="large">
                Process
              </Button>
            )}
            <Button onClick={handleComplete} color="primary" size="large">
              Complete
            </Button>
          </div>
        )}
      </CardActions>
    </CrewCard>
  ) : null;
};
